/** @format */
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { TimelineSide } from '../../config/constants';
import { FanCard } from '../fan-card';
import { DEFAULT_TEST_ID } from './config/constants';
import { FanHandProps } from './config/types';
import { styles } from './fan-hand.styles';
import { getFanCardAngle } from './style-helpers';

const StyledFanHand = styled.ol`
    ${styles}
`;

function getSideLabel(side: TimelineSide, t: (key: string) => string): string {
    switch (side) {
        case TimelineSide.BEFORE:
            return t('Owned before this model');
        case TimelineSide.AFTER:
            return t('Added after this model');
        default: {
            const exhaustiveCheck: never = side;
            return exhaustiveCheck;
        }
    }
}

export const FanHand: FunctionComponent<FanHandProps> = ({
    models,
    side,
    selectedModelId,
    onSelect,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();

    /*
     * An empty hand renders nothing at all rather than an empty <ol> - a
     * hand-less side would leave a dangling half-axis with nothing on it.
     */
    if (models.length === 0) {
        return null;
    }

    return (
        <StyledFanHand
            aria-label={getSideLabel(side, t)}
            data-testid={dataTestId}
            {...otherProps}
        >
            {models.map((model, index) => (
                <FanCard
                    key={model.id}
                    model={model}
                    angle={getFanCardAngle(index, models.length)}
                    isSelected={model.id === selectedModelId}
                    onSelect={onSelect}
                    data-testid={`${dataTestId}-card-${index}`}
                />
            ))}
        </StyledFanHand>
    );
};

export * from './config/constants';
export type { FanHandProps } from './config/types';
